<template>
    <div>
        <!-- Controls stay put while the day scrolls under them. -->
        <div class="sticky top-0 z-10 -mx-4 px-4 pt-3 pb-3 bg-gray-100/95 backdrop-blur-sm">
            <div class="flex items-center justify-between gap-3 mb-1">
                <h1 class="text-xl font-bold tracking-tight">Book a table</h1>
                <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full" :class="openingChip.class">
                    <i class="text-xs" :class="[openingChip.icon, openingChip.iconClass]" aria-hidden="true" />
                    <span class="text-sm font-semibold">{{ openingChip.label }}</span>
                </div>
            </div>

            <DayStrip :days="dayEntries" @select="selectDay" />

            <TablePills
                v-if="isOpen"
                :tables="tableEntries"
                class="mt-1"
                @select="selectedSpaceIndex = $event"
            />

            <div v-if="isOpen && selectedSpace" class="flex items-center justify-between gap-2 mt-1.5">
                <span class="text-sm text-gray-600">{{ selectedSpaceSummary }}</span>
                <button
                    type="button"
                    class="shrink-0 flex items-center gap-1.5 min-h-11 -mr-1 px-1 text-sm font-semibold text-primary-800 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                    :aria-label="`Table details for ${selectedSpace.name}`"
                    @click="$emit('viewSpace', selectedSpace)"
                >
                    Table details
                    <i class="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                </button>
            </div>
        </div>

        <!-- Closed -->
        <div v-if="!isOpen" class="mt-6 p-7 bg-white border border-secondary-200 rounded-2xl text-center">
            <i class="fa-solid fa-lock text-secondary text-3xl" aria-hidden="true" />
            <h2 class="mt-3 text-lg font-bold">We're closed on {{ closedDayLabel }}</h2>
            <p class="mt-1 text-base text-gray-600">
                {{ currentDayOverride?.reason || "We're not taking bookings on this day." }}
            </p>
            <button
                v-if="nextOpenDay"
                type="button"
                class="mt-4 inline-flex items-center justify-center h-12 px-5 rounded-lg bg-primary text-sm font-bold uppercase tracking-widest text-white transition hover:bg-tertiary hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                @click="selectDay(nextOpenDay)"
            >
                Jump to {{ nextOpenDay.toFormat("ccc d") }}
            </button>
        </div>

        <template v-else>
            <div
                v-if="currentDayOverride"
                class="flex items-start gap-2 mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg"
            >
                <i class="fa-solid fa-circle-info text-amber-700 mt-0.5" aria-hidden="true" />
                <div>
                    <div class="text-sm font-bold text-amber-900">{{ overrideHeading }}</div>
                    <div v-if="currentDayOverride.reason" class="text-sm text-amber-900">
                        {{ currentDayOverride.reason }}
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <button
                    v-if="hiddenPastCount && !showPast"
                    type="button"
                    class="flex items-center gap-2 w-full min-h-11 mb-1 pl-14 text-sm font-semibold text-gray-600 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                    @click="showPast = true"
                >
                    <span>Show {{ hiddenPastCount }} earlier hour{{ hiddenPastCount === 1 ? "" : "s" }}</span>
                    <i class="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
                </button>

                <SlotRow
                    v-for="cell in visibleCells"
                    :key="cell.hour"
                    :cell="cell"
                    :price-hint="priceHint"
                    @book="bookSlot"
                    @view-event="viewEvent"
                />

                <p class="mt-1.5 pl-14 text-sm text-gray-600">Closes at {{ closingLabel }}</p>
            </div>
        </template>
    </div>
</template>

<script setup>
    import { computed, ref, watch } from "vue";
    import { DateTime } from "luxon";
    import { router } from "@inertiajs/vue3";
    import DayStrip from "./DayStrip.vue";
    import TablePills from "./TablePills.vue";
    import SlotRow from "./SlotRow.vue";
    import { getDateFromISO } from "../../Helpers/DateHelper.js";
    import {
        buildDaySchedule,
        countFreeSlots,
        dayEntriesFor,
        formatHourLong,
        nextOpenDayAfter,
        openingLabel,
        priceHintFor,
        SLOT_PAST,
        spaceSummary,
        specialHoursHeading,
    } from "../../Composables/useBookingSchedule.js";

    const props = defineProps({
        defineHours: Object,
        currentDay: Object,
        computedCalendarView: Object,
        spaces: Object,
        events: Object,
        currentDayOverride: Object,
        openingTimes: Object,
        openingTimeOverrides: Object,
    });

    const emits = defineEmits(["changeCurrentDay", "purchaseBlock", "viewSpace"]);

    const selectedSpaceIndex = ref(0);
    const showPast = ref(false);

    const isOpen = computed(() => !!props.defineHours && props.defineHours.length > 0);

    const selectedSpaceView = computed(
        () => props.computedCalendarView?.[selectedSpaceIndex.value] || props.computedCalendarView?.[0] || null,
    );

    const selectedSpace = computed(() => selectedSpaceView.value?.space || null);

    const scheduleFor = (spaceView, day = props.currentDay, hours = props.defineHours) =>
        buildDaySchedule({ day, spaceView, events: props.events, hours });

    const cells = computed(() => (isOpen.value ? scheduleFor(selectedSpaceView.value) : []));

    const hiddenPastCount = computed(() => cells.value.filter((cell) => cell.kind === SLOT_PAST).length);

    const visibleCells = computed(() =>
        showPast.value ? cells.value : cells.value.filter((cell) => cell.kind !== SLOT_PAST),
    );

    // Collapse the finished hours again whenever the day or table changes.
    watch([() => props.currentDay, selectedSpaceIndex], () => (showPast.value = false));

    const dayEntries = computed(() =>
        dayEntriesFor({
            currentDay: props.currentDay,
            calendarView: props.computedCalendarView,
            events: props.events,
            openingTimes: props.openingTimes,
            overrides: props.openingTimeOverrides,
        }),
    );

    const tableEntries = computed(() =>
        (props.computedCalendarView || []).map((spaceView, index) => {
            const free = countFreeSlots({
                day: props.currentDay,
                spaceView: spaceView,
                events: props.events,
                hours: props.defineHours,
            });

            return {
                id: spaceView.space.id,
                index: index,
                name: spaceView.space.name,
                note: free === 0 ? "Full" : "Spaces available",
                selected: index === selectedSpaceIndex.value,
            };
        }),
    );

    const selectedSpaceSummary = computed(() => spaceSummary(selectedSpace.value));

    const priceHint = computed(() => priceHintFor(selectedSpace.value));

    const openingChip = computed(() => {
        if (!isOpen.value) {
            return {
                label: "Closed",
                icon: "fa-solid fa-lock",
                class: "bg-secondary-50 text-secondary-900",
                iconClass: "text-secondary",
            };
        }

        const label = openingLabel(props.defineHours);

        return props.currentDayOverride
            ? {
                  label: label,
                  icon: "fa-solid fa-clock",
                  class: "bg-amber-50 text-amber-900",
                  iconClass: "text-amber-700",
              }
            : {
                  label: label,
                  icon: "fa-solid fa-clock",
                  class: "bg-gray-200 text-gray-700",
                  iconClass: "text-gray-500",
              };
    });

    const closingLabel = computed(() =>
        isOpen.value ? formatHourLong(props.defineHours[props.defineHours.length - 1].time + 1) : "",
    );

    const closedDayLabel = computed(() => props.currentDay.toFormat("cccc d"));

    const overrideHeading = computed(() => specialHoursHeading(props.currentDay));

    const nextOpenDay = computed(() =>
        nextOpenDayAfter(props.currentDay, props.openingTimes, props.openingTimeOverrides),
    );

    const selectDay = (day) => {
        if (!day.hasSame(props.currentDay, "day")) {
            emits("changeCurrentDay", DateTime.fromJSDate(day.toJSDate()));
        }
    };

    // Same payload the grid emits, so Home.vue's handler is untouched.
    const bookSlot = (cell) => emits("purchaseBlock", null, cell.hour, selectedSpace.value);

    const viewEvent = (cell) => {
        if (cell.eventId) {
            router.get(`/event/${cell.eventId}/${getDateFromISO(cell.startsAt.toISO()).toSQL()}`);
        }
    };

    // Keep the pill selection valid if the space list ever changes underneath us.
    watch(
        () => props.computedCalendarView?.length,
        (length) => {
            if (length && selectedSpaceIndex.value >= length) {
                selectedSpaceIndex.value = 0;
            }
        },
    );
</script>
